import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import axios from "axios";
import { format } from "date-fns";
import {
    FormError,
    FormField,
    SentMessage,
    SpamProtection,
    turnstileRequired,
} from "../site/FormBits";

// Anfrage für Feier oder Catering → POST /api/public/contact mit type "feier".
const schema = Yup.object().shape({
    name: Yup.string().trim().required("Name ist erforderlich."),
    phoneNumber: Yup.string()
        .trim()
        .matches(/^(\+?[0-9\s-]{8,20})?$/, "Ungültige Telefonnummer.")
        .test("kontaktweg", "Bitte Telefon oder E-Mail angeben.", function (value) {
            return Boolean(value || this.parent.email);
        }),
    email: Yup.string().trim().email("Ungültige E-Mail-Adresse."),
    occasion: Yup.string().max(200),
    eventDate: Yup.string(),
    guests: Yup.number()
        .transform((value, original) => (original === "" ? undefined : value))
        .typeError("Bitte eine Zahl.")
        .integer("Bitte eine ganze Zahl.")
        .min(1, "Mindestens eine Person."),
    eventKind: Yup.string().oneOf(["restaurant", "catering"]).required(),
    message: Yup.string().max(2000),
    website: Yup.string().max(0, "Ungültige Anfrage"),
    formStartedAt: Yup.number().required(),
    turnstileToken: Yup.string().test(
        "turnstile-required",
        "Bitte bestätigen Sie, dass Sie kein Roboter sind.",
        turnstileRequired,
    ),
});

export default function EventRequestForm() {
    const [sent, setSent] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [loading, setLoading] = useState(false);
    const turnstileRef = useRef(null);

    const {
        register,
        handleSubmit,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: { eventKind: "restaurant" },
    });

    useEffect(() => {
        setValue("formStartedAt", Date.now());
        setValue("website", "");
    }, [setValue]);

    const onSubmit = async (data) => {
        setLoading(true);
        setErrorMessage(null);
        try {
            await axios.post("/api/public/contact", { ...data, type: "feier" });
            setSent(true);
        } catch (error) {
            setErrorMessage(
                error?.response?.data?.message ||
                    "Fehler beim Senden der Anfrage. Bitte versuchen Sie es erneut.",
            );
            turnstileRef.current?.reset();
        } finally {
            setLoading(false);
        }
    };

    if (sent) {
        return <SentMessage title="Danke, wir melden uns in Kürze." />;
    }

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="reveal d2 grid min-w-0 grid-cols-1 desk:grid-cols-2 gap-4"
        >
            <FormField
                label="Name"
                autoComplete="name"
                className="col-span-full"
                error={errors.name?.message}
                {...register("name")}
            />
            <FormField
                label="Telefon"
                type="tel"
                autoComplete="tel"
                error={errors.phoneNumber?.message}
                {...register("phoneNumber")}
            />
            <FormField
                label="E-Mail"
                type="email"
                autoComplete="email"
                error={errors.email?.message}
                {...register("email")}
            />
            <FormField label="Anlass" placeholder="z. B. Geburtstag, Taufe" {...register("occasion")} />
            <FormField
                label="Datum"
                type="date"
                min={format(new Date(), "yyyy-MM-dd")}
                {...register("eventDate")}
            />
            <FormField
                label="Personen"
                type="number"
                min={1}
                inputMode="numeric"
                error={errors.guests?.message}
                {...register("guests")}
            />
            <fieldset className="col-span-full flex flex-wrap gap-x-6 gap-y-2 py-1.5 text-sm text-forest/70">
                <legend className="sr-only">Art der Veranstaltung</legend>
                <label className="flex items-center gap-2">
                    <input type="radio" value="restaurant" className="accent-forest" {...register("eventKind")} />
                    Feier bei uns im Restaurant
                </label>
                <label className="flex items-center gap-2">
                    <input type="radio" value="catering" className="accent-forest" {...register("eventKind")} />
                    Catering bei Ihnen
                </label>
            </fieldset>
            <FormField
                label="Nachricht"
                as="textarea"
                rows={3}
                className="col-span-full"
                error={errors.message?.message}
                {...register("message")}
            />
            <SpamProtection
                register={register}
                setValue={setValue}
                turnstileRef={turnstileRef}
                error={errors.turnstileToken?.message}
            />
            <FormError>{errorMessage}</FormError>
            <button type="submit" disabled={loading} className="btn btn-primary col-span-full mt-2 py-[18px]">
                {loading ? "Wird gesendet …" : "Anfrage absenden"}
            </button>
        </form>
    );
}
